import Contracts from '@tokenysolutions/t-rex';
import { Contract, providers, Signer } from 'ethers';

import { contractFactory, OverrideTransactionsInterface } from '../utils/utils';

/**
 * Deploys IdentityRegistry on Ethereum's Blockchain
 * @dev ClaimTopics, TrustedIssuers, IdentityRegistryStorage must be deployed first
 * @param {{ClaimTopics: Contract; TrustedIssuers: Contract; IdentityRegistryStorage: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>}
 */
export async function deployIdentityRegistry(contract: {
  ClaimTopics: Contract;
  TrustedIssuers: Contract;
  IdentityRegistryStorage: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.IdentityRegistry.abi,
    bytecode: Contracts.contracts.IdentityRegistry.bytecode,
    signer: contract.signer,
  });
  const deploy = await factory.deploy(contract.TrustedIssuers.address, contract.ClaimTopics.address, contract.IdentityRegistryStorage.address);
  return await deploy.deployed();
}

/**
 * Register an identity contract corresponding to a user address.
 * @param {{identity: IdentityInterface; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function registerIdentity(tx: {
  userAddress: string;
  identityAddress: string;
  country: number;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.registerIdentity(tx.userAddress, tx.identityAddress, tx.country, tx.overrides);
}

/**
 * Registers identities in batch
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchRegisterIdentities(tx: {
  userAddresses: string[];
  identityAddresses: string[];
  countries: number[];
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.batchRegisterIdentity(tx.userAddresses, tx.identityAddresses, tx.countries, tx.overrides);
}

/**
 * Updates an identity contract corresponding to a user address.
 * @param {{wallet: string; identity: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function updateIdentity(tx: {
  userAddress: string;
  identity: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.updateIdentity(tx.userAddress, tx.identity, tx.overrides);
}

/**
 * Updates the country corresponding to a user address.
 * @param {{wallet: string; country: number; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function updateCountry(tx: {
  wallet: string;
  country: number;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.updateCountry(tx.wallet, tx.country, tx.overrides);
}

/**
 * Removes an user from the identity registry.
 * @param {{wallet: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function deleteIdentity(tx: {
  wallet: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.deleteIdentity(tx.wallet, tx.overrides);
}

/**
 * Checks whether an Address has the required claims or not based
 * on trusted issuers registry and from the claim topics
 * @param {{wallet: string; registry: Contract}} tx
 * @returns {Promise<boolean>}
 */
export async function isVerified(tx: { wallet: string; registry: Contract }): Promise<boolean> {
  return await tx.registry.isVerified(tx.wallet);
}

/**
 * Returns the address of the TrustedIssuersRegistry linked to this contract
 * @param {{registry: Contract}} tx
 * @returns {Promise<Contract>}
 */
export async function getTrustedIssuersRegistry(tx: { registry: Contract }): Promise<Contract> {
  return await tx.registry.issuersRegistry();
}

/**
 * Returns the address of the ClaimTopicsRegistry linked to this contract
 * @param {{registry: Contract}} tx
 * @returns {Promise<Contract>}
 */
export async function getClaimTopicsRegistry(tx: { registry: Contract }): Promise<Contract> {
  return await tx.registry.topicsRegistry();
}

/**
 * Returns the address of the IdentityRegistryStorage linked to this contract
 * @param {{registry: Contract}} tx
 * @returns {Promise<Contract>}
 */
export async function getIdentityRegistryStorage(tx: { registry: Contract }): Promise<Contract> {
  return await tx.registry.identityStorage();
}

/**
 * Replace the actual identityRegistryStorage contract with a new one.
 * @param {{identityRegistryStorage: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setIdentityRegistryStorage(tx: {
  identityRegistryStorage: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.setIdentityRegistryStorage(tx.identityRegistryStorage, tx.overrides);
}

/**
 * Replace the actual claimTopicsRegistry contract with a new one.
 * @param {{newClaimTopicsRegistry: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setClaimTopicsRegistry(tx: {
  newClaimTopicsRegistry: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.setClaimTopicsRegistry(tx.newClaimTopicsRegistry, tx.overrides);
}

/**
 * Replace the actual trustedIssuersRegistry contract with a new one.
 * @param {{newTrustedIssuersRegistry: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setTrustedIssuersRegistry(tx: {
  newTrustedIssuersRegistry: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.setTrustedIssuersRegistry(tx.newTrustedIssuersRegistry, tx.overrides);
}

/**
 * Checks whether a wallet has its Identity registered or not
 * @param {{walletAddress: string; registry: Contract}} tx
 * @returns {Promise<boolean>}
 */
export async function contains(tx: { address: string; registry: Contract }): Promise<boolean> {
  return await tx.registry.contains(tx.address);
}

/**
 * Checks whether an address has Agent Role
 * @param {{walletAddress: string; registry: Contract}} tx
 * @returns {Promise<boolean>}
 */
export async function isAgent(tx: { address: string; registry: Contract }): Promise<boolean> {
  return await tx.registry.isAgent(tx.address);
}

/**
 * Adds an address as _agent of the Identity Registry Contract.
 * @param {{agent: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function addAgentToIdentityRegistry(tx: {
  agent: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.addAgentOnIdentityRegistryContract(tx.agent, tx.overrides);
}

/**
 * Removes an address from being _agent of the Identity Registry Contract.
 * @param {{agent: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function removeAgentFromIdentityRegistry(tx: {
  agent: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.removeAgentOnIdentityRegistryContract(tx.agent, tx.overrides);
}

/**
 * Transfers the Ownership of the Identity Registry to a new Owner.
 * @param {{newOwner: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferIdentityRegistryOwnership(tx: {
  newOwner: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.registry.transferOwnershipOnIdentityRegistryContract(tx.newOwner, tx.overrides);
}

/**
 * Returns the Owner of the Identity Registry.
 * @param {{registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfIdentityRegistry(tx: { registry: Contract }): Promise<string> {
  return await tx.registry.owner();
}
